/**
 * Command registry and execution logic
 */

import { Command } from 'commander';
import { CacheCommand, CacheOptionsSchema } from './commands/cache';
import {
  LastFailedCommand,
  LastFailedOptionsSchema,
} from './commands/last-failed';
import { UploadCommand } from './commands/upload';
import { ValidationError, CLIOptions, Config } from '@/types';
import { VERSION } from '@/version';
import {
  printHelpHeader,
  printHelpSection,
  printHelpCommand,
  printHelpOption,
  printHelpExample,
  printHelpEnvVar,
  colors,
} from '@/utils/ui';

/**
 * Command manager for handling subcommands and backward compatibility
 */
export class CommandManager {
  private readonly program: Command;
  private config?: Config;

  constructor() {
    this.program = new Command();
    this.setupProgram();
  }

  /**
   * Setup the main program with subcommands and backward compatibility
   */
  private setupProgram(): void {
    this.program
      .name('tdpw')
      .description('TestDino CLI - Upload reports and cache test metadata')
      .version(VERSION, '--version', 'Display version')
      .helpOption(false);

    // Cache subcommand (store metadata)
    this.program
      .command('cache')
      .description('Store test execution metadata after Playwright runs')
      .option(
        '--cache-id <value>',
        'Custom cache ID (or TESTDINO_CACHE_ID env var)'
      )
      .option(
        '--working-dir <path>',
        'Directory to scan for test results (default: cwd)'
      )
      .option(
        '-t, --token <value>',
        'TestDino API token (or TESTDINO_TOKEN env var)'
      )
      .option('-v, --verbose', 'Verbose logging')
      .action(async options => {
        await this.executeCache(options);
      });

    // Last Failed subcommand (retrieve failure data)
    this.program
      .command('last-failed')
      .description('Get last failed test cases for Playwright execution')
      .option(
        '--cache-id <value>',
        'Custom cache ID (or TESTDINO_CACHE_ID env var)'
      )
      .option(
        '--branch <name>',
        'Override branch name (auto-detected from Git by default)'
      )
      .option(
        '--commit <hash>',
        'Override commit hash (auto-detected from Git by default)'
      )
      .option(
        '--shard <value>',
        'Retrieve failures for specific shard (format: index/total, e.g., "1/3")'
      )
      .option(
        '-t, --token <value>',
        'TestDino API token (or TESTDINO_TOKEN env var)'
      )
      .option('-v, --verbose', 'Verbose logging')
      .action(async options => {
        await this.executeLastFailed(options);
      });

    // Upload subcommand for backward compatibility
    this.program
      .command('upload')
      .description('Upload test reports to TestDino')
      .arguments('[report-directory]')
      .option('-t, --token <value>', 'TestDino API token')
      .option(
        '--environment <value>',
        'Target environment tag (e.g., staging, production, qa)'
      )
      .option('--upload-images', 'Upload image attachments', false)
      .option('--upload-videos', 'Upload video attachments', false)
      .option('--upload-html', 'Upload HTML reports', false)
      .option('--upload-traces', 'Upload trace files', false)
      .option('--upload-files', 'Upload file attachments', false)
      .option('--upload-full-json', 'Upload all attachments', false)
      .option('--json-report <path>', 'Specific JSON report path')
      .option('--html-report <path>', 'Specific HTML report path')
      .option('--trace-dir <path>', 'Specific trace directory path')
      .option('-v, --verbose', 'Verbose logging', false)
      .action(async (reportDirectory, options) => {
        await this.executeUpload(reportDirectory, options);
      });
  }

  /**
   * Execute cache command
   */
  private async executeCache(options: Record<string, unknown>): Promise<void> {
    try {
      // Validate cache options
      const validatedOptions = CacheOptionsSchema.parse(options);

      // Create configuration for cache command
      const config = this.getOrCreateConfig(validatedOptions.token);

      // Execute cache command
      const cacheCommand = new CacheCommand(config);
      await cacheCommand.execute(validatedOptions);
    } catch (error) {
      throw new ValidationError(
        error instanceof Error ? error.message : 'Cache command failed'
      );
    }
  }

  /**
   * Execute last failed command
   */
  private async executeLastFailed(
    options: Record<string, unknown>
  ): Promise<void> {
    try {
      // Validate last-failed options
      const validatedOptions = LastFailedOptionsSchema.parse(options);

      // Create configuration for last failed command
      const config = this.getOrCreateConfig(validatedOptions.token);

      // Execute last failed command
      const lastFailedCommand = new LastFailedCommand(config);
      await lastFailedCommand.execute(validatedOptions);
    } catch (error) {
      throw new ValidationError(
        error instanceof Error ? error.message : 'Last failed command failed'
      );
    }
  }

  /**
   * Execute upload command (backward compatibility)
   */
  private async executeUpload(
    reportDirectory: string,
    options: Record<string, unknown>
  ): Promise<void> {
    if (!reportDirectory) {
      throw new ValidationError(
        'Report directory is required for upload command. Use "npx tdpw cache" for caching metadata.'
      );
    }

    // Resolve environment value: CLI option > env var > default
    const resolvedEnvironment = this.resolveEnvironmentValue(
      options.environment as string | undefined
    );

    // Build CLIOptions for upload command
    const cliOptions: CLIOptions = {
      reportDirectory,
      token: (options.token as string) || '',
      uploadImages: Boolean(options.uploadImages),
      uploadVideos: Boolean(options.uploadVideos),
      uploadHtml: Boolean(options.uploadHtml),
      uploadTraces: Boolean(options.uploadTraces),
      uploadFiles: Boolean(options.uploadFiles),
      uploadFullJson: Boolean(options.uploadFullJson),
      jsonReport: options.jsonReport as string | undefined,
      htmlReport: options.htmlReport as string | undefined,
      traceDir: options.traceDir as string | undefined,
      verbose: Boolean(options.verbose),
      environment: resolvedEnvironment,
    };

    // Create configuration and execute
    this.getOrCreateConfig(cliOptions.token); // Ensures config is created with token
    const uploadCommand = new UploadCommand();
    await uploadCommand.execute(cliOptions);
  }

  /**
   * Resolve environment value with precedence: CLI option > env var > default
   * Validates format using regex: /^[a-z0-9]+([_-][a-z0-9]+)*$/
   */
  private resolveEnvironmentValue(cliValue?: string): string {
    const defaultValue = 'unknown';
    const envVarValue = process.env.TESTDINO_TARGET_ENV;

    // Determine the resolved value based on precedence
    let resolvedValue: string;
    if (cliValue !== undefined && cliValue !== '') {
      resolvedValue = cliValue;
    } else if (envVarValue !== undefined && envVarValue !== '') {
      resolvedValue = envVarValue;
    } else {
      resolvedValue = defaultValue;
    }

    // Skip validation for default value
    if (resolvedValue === defaultValue) {
      return resolvedValue;
    }

    // Validate format using regex
    const environmentPattern = /^[a-z0-9]+([_-][a-z0-9]+)*$/;
    if (!environmentPattern.test(resolvedValue)) {
      throw new ValidationError(
        `Invalid environment value "${resolvedValue}". ` +
          'Must contain only lowercase letters, numbers, hyphens, and underscores. ' +
          'Examples: staging, production, qa, dev-1, test_env'
      );
    }

    return resolvedValue;
  }

  /**
   * Get or create configuration for cache command
   */
  private getOrCreateConfig(token?: string): Config {
    if (!this.config) {
      this.config = this.createConfigForCache(token);
    }
    return this.config;
  }

  /**
   * Create configuration specifically for cache command
   */
  private createConfigForCache(token?: string): Config {
    // Resolve token from CLI option or environment variable
    const resolvedToken = token || process.env.TESTDINO_TOKEN || '';

    if (!resolvedToken) {
      throw new ValidationError(
        'API token is required. Provide via --token flag or TESTDINO_TOKEN environment variable.'
      );
    }

    // Create minimal config for cache command
    const config: Config = {
      apiUrl: process.env.TESTDINO_API_URL || 'https://api.testdino.com',
      token: resolvedToken,
      uploadImages: false,
      uploadVideos: false,
      uploadHtml: false,
      uploadTraces: false,
      uploadFiles: false,
      uploadFullJson: false,
      verbose: false,
      environment: 'unknown', // Not used by cache command
      batchSize: 5,
      maxConcurrentUploads: 10,
      uploadTimeout: 60000,
      retryAttempts: 3,
    };

    return config;
  }

  /**
   * Parse and execute commands
   */
  async parseAndExecute(args: string[] = process.argv): Promise<void> {
    try {
      // Note: Help handling is now done in CLI index.ts before reaching here

      await this.program.parseAsync(args);
    } catch (error) {
      // Re-throw with better context
      throw new ValidationError(
        error instanceof Error ? error.message : 'Command execution failed'
      );
    }
  }

  /**
   * Show main help
   */
  showHelp(): void {
    printHelpHeader(VERSION, 'Upload reports and cache test metadata');

    printHelpSection('Commands');
    printHelpCommand('upload <dir>', 'Upload Playwright reports');
    printHelpCommand('cache', 'Store test metadata for reruns');
    printHelpCommand('last-failed', 'Get previously failed tests');

    printHelpSection('Quick Start');
    printHelpExample('npx tdpw upload ./playwright-report --token="..."');
    printHelpExample('npx tdpw cache --token="..."');
    printHelpExample('npx playwright test $(npx tdpw last-failed)');

    printHelpSection('Environment Variables');
    printHelpEnvVar('TESTDINO_TOKEN', 'API authentication token');
    printHelpEnvVar('TESTDINO_TARGET_ENV', 'Test run target environment tag');

    console.log(
      `\n  ${colors.dim('Run')} tdpw <command> --help ${colors.dim('for command details')}`
    );
    console.log(`  ${colors.dim('Docs:')} https://docs.testdino.com/cli\n`);
  }

  /**
   * Show cache command help
   */
  showCacheHelp(): void {
    console.log(`\n${colors.bold('Usage:')} tdpw cache [options]\n`);
    console.log(
      `${colors.dim('Store test execution metadata after Playwright runs')}\n`
    );

    printHelpSection('Options');
    printHelpOption('-t, --token <value>', 'API token (or TESTDINO_TOKEN)');
    printHelpOption('--cache-id <value>', 'Custom cache ID');
    printHelpOption('--working-dir <path>', 'Directory to scan');
    printHelpOption('-v, --verbose', 'Verbose logging');

    printHelpSection('Examples');
    printHelpExample('npx tdpw cache --token="..."');
    printHelpExample('npx tdpw cache --working-dir ./test-results');
    console.log();
  }

  /**
   * Show last-failed command help
   */
  showLastFailedHelp(): void {
    console.log(`\n${colors.bold('Usage:')} tdpw last-failed [options]\n`);
    console.log(
      `${colors.dim('Get previously failed tests for selective reruns')}\n`
    );

    printHelpSection('Options');
    printHelpOption('-t, --token <value>', 'API token (or TESTDINO_TOKEN)');
    printHelpOption('--cache-id <value>', 'Custom cache ID');
    printHelpOption('--branch <name>', 'Override branch name');
    printHelpOption('--commit <hash>', 'Override commit hash');
    printHelpOption('--shard <value>', 'Filter by shard (e.g., 1/3)');
    printHelpOption('-v, --verbose', 'Verbose logging');

    printHelpSection('Examples');
    printHelpExample('npx tdpw last-failed --token="..."');
    printHelpExample('npx playwright test $(npx tdpw last-failed)');
    console.log();
  }

  /**
   * Show upload command help
   */
  showUploadHelp(): void {
    console.log(
      `\n${colors.bold('Usage:')} tdpw upload <report-dir> [options]\n`
    );
    console.log(
      `${colors.dim('Upload Playwright test reports to TestDino')}\n`
    );

    printHelpSection('Options');
    printHelpOption('-t, --token <value>', 'API token (or TESTDINO_TOKEN)');
    printHelpOption(
      '--environment <value>',
      'Environment tag (staging, production)'
    );
    printHelpOption('--upload-full-json', 'Upload all attachments');
    printHelpOption('--upload-images', 'Upload image attachments');
    printHelpOption('--upload-videos', 'Upload video attachments');
    printHelpOption('--upload-traces', 'Upload trace files');
    printHelpOption('--upload-html', 'Upload HTML reports');
    printHelpOption('-v, --verbose', 'Verbose logging');

    printHelpSection('Examples');
    printHelpExample('npx tdpw upload ./playwright-report --token="..."');
    printHelpExample('npx tdpw upload ./playwright-report --upload-full-json');
    printHelpExample(
      'npx tdpw upload ./playwright-report --environment="staging"'
    );
    console.log();
  }

  /**
   * Get version
   */
  getVersion(): string {
    return VERSION;
  }
}

/**
 * Export command manager instance
 */
export const commandManager = new CommandManager();
